import { useState, useEffect, useRef } from 'react';

const API = '/api';

const THEME_SWATCHES = {
  noir:            'linear-gradient(135deg, #000000 60%, #444444)',
  midnight_blue:   'linear-gradient(135deg, #0a1628 60%, #c9a84c)',
  blueprint:       'linear-gradient(135deg, #0a2a5e 60%, #4a9eff)',
  neon_cyberpunk:  'linear-gradient(135deg, #0d0d1a 60%, #ff00ff)',
  warm_beige:      'linear-gradient(135deg, #c8ad8a 60%, #8b6a42)',
  pastel_dream:    'linear-gradient(135deg, #d4c5e2 60%, #b8d4c8)',
  japanese_ink:    'linear-gradient(135deg, #f5f0e8 60%, #2a2a2a)',
  emerald:         'linear-gradient(135deg, #0a2a1a 60%, #2a8a4a)',
  forest:          'linear-gradient(135deg, #1a2a1a 60%, #4a7a4a)',
  ocean:           'linear-gradient(135deg, #0a1a3a 60%, #2a6a9a)',
  terracotta:      'linear-gradient(135deg, #8b3a2a 60%, #c87a4a)',
  sunset:          'linear-gradient(135deg, #8b2a1a 60%, #e8a84a)',
  autumn:          'linear-gradient(135deg, #5a2a0a 60%, #c86a1a)',
  copper_patina:   'linear-gradient(135deg, #2a4a3a 60%, #8abaa0)',
  monochrome_blue: 'linear-gradient(135deg, #0a1a3a 60%, #4a7aaa)',
  gradient_roads:  'linear-gradient(135deg, #1a1a2a 60%, #6a5acd)',
  contrast_zones:  'linear-gradient(135deg, #0a0a0a 60%, #ff4444)',
};

// Size presets — width/height in inches at 300 DPI
const SIZE_PRESETS = [
  { id: 'default',   label: 'Default',    sub: '1800 × 2400 px',  width: 6.0,  height: 8.0  },
  { id: 'instagram', label: 'Instagram',  sub: '1080 × 1080 px',  width: 3.6,  height: 3.6  },
  { id: 'a4',        label: 'A4 Print',   sub: '2480 × 3508 px',  width: 8.3,  height: 11.7 },
  { id: '4k',        label: '4K',         sub: '3840 × 2160 px',  width: 12.8, height: 7.2  },
];

const FONT_GROUPS = [
  {
    label: 'Serif',
    fonts: [
      { id: 'Playfair Display', label: 'Playfair', css: "'Playfair Display', serif" },
      { id: 'Cormorant Garamond', label: 'Cormorant', css: "'Cormorant Garamond', serif" },
      { id: 'Libre Baskerville', label: 'Baskerville', css: "'Libre Baskerville', serif" },
      { id: 'EB Garamond', label: 'Garamond', css: "'EB Garamond', serif" },
      { id: 'Lora', label: 'Lora', css: "'Lora', serif" },
    ],
  },
  {
    label: 'Sans-serif',
    fonts: [
      { id: 'Montserrat', label: 'Montserrat', css: "'Montserrat', sans-serif" },
      { id: 'Raleway', label: 'Raleway', css: "'Raleway', sans-serif" },
      { id: 'Josefin Sans', label: 'Josefin', css: "'Josefin Sans', sans-serif" },
      { id: 'Outfit', label: 'Outfit', css: "'Outfit', sans-serif" },
      { id: 'Funnel Display', label: 'Funnel', css: "'Funnel Display', sans-serif" },
    ],
  },
  {
    label: 'Mono',
    fonts: [
      { id: 'Space Mono', label: 'Space Mono', css: "'Space Mono', monospace" },
      { id: 'Courier Prime', label: 'Courier', css: "'Courier Prime', monospace" },
      { id: 'Share Tech Mono', label: 'Share Tech', css: "'Share Tech Mono', monospace" },
      { id: 'Azeret Mono', label: 'Azeret', css: "'Azeret Mono', monospace" },
    ],
  },
];

const ALL_FONTS = FONT_GROUPS.flatMap(g => g.fonts);
const GFONTS_URL =
  'https://fonts.googleapis.com/css2?family=' +
  ALL_FONTS.map(f => f.id.replace(/ /g, '+') + ':wght@400;600').join('&family=') +
  '&display=swap';

function SizeSelector({ selected, onSelect }) {
  return (
    <div className="space-y-1.5">
      <label className="font-body text-xs text-ash tracking-widest uppercase">Output Size</label>
      <div className="grid grid-cols-2 gap-2">
        {SIZE_PRESETS.map(preset => (
          <button
            key={preset.id}
            onClick={() => onSelect(preset)}
            className={`px-3 py-2 rounded-lg border text-left transition-all duration-200 ${
              selected.id === preset.id
                ? 'bg-gold/10 border-gold text-white'
                : 'bg-ink-800 border-white/8 text-white/60 hover:border-white/20 hover:text-white'
            }`}
          >
            <p className="font-body text-xs font-medium">{preset.label}</p>
            <p className="font-body text-[10px] text-ash/60 mt-0.5">{preset.sub}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function FontSelector({ selected, onSelect }) {
  return (
    <div className="space-y-3">
      <label className="font-body text-xs text-ash tracking-widest uppercase block">Label Font</label>
      {FONT_GROUPS.map(group => (
        <div key={group.label}>
          <p className="font-body text-[10px] text-ash/40 uppercase tracking-widest mb-1.5">{group.label}</p>
          <div className="flex flex-wrap gap-2">
            {group.fonts.map(font => {
              const isSelected = selected === font.id;
              return (
                <button
                  key={font.id}
                  onClick={() => onSelect(isSelected ? null : font.id)}
                  style={{ fontFamily: font.css }}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-all duration-200 ${
                    isSelected
                      ? 'bg-gold text-ink-950 border-gold'
                      : 'bg-ink-800 text-white/70 border-white/10 hover:border-white/30 hover:text-white'
                  }`}
                >
                  {font.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {selected && (
        <p className="font-body text-[10px] text-ash/50">Click the selected font again to use the default.</p>
      )}
    </div>
  );
}

function AdvancedSection({ lat, setLat, lng, setLng }) {
  const [open, setOpen] = useState(false);
  const hasValues = lat.trim() || lng.trim();

  return (
    <div className="rounded-lg border border-white/8 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-ink-800 hover:bg-ink-700 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="font-body text-xs text-ash tracking-widest uppercase">Advanced</span>
          {hasValues && (
            <span className="w-1.5 h-1.5 rounded-full bg-gold" title="Custom coordinates set" />
          )}
        </div>
        <svg
          className={`w-3.5 h-3.5 text-ash transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="px-4 py-4 bg-ink-900 space-y-3 border-t border-white/5">
          <p className="font-body text-[11px] text-ash/50 leading-relaxed">
            Override the map centre point. If set, the city/country fields are still used for the poster label but geocoding is skipped.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="font-body text-[10px] text-ash/60 uppercase tracking-widest">Latitude</label>
              <input
                value={lat}
                onChange={e => setLat(e.target.value)}
                placeholder="48.8566"
                className="w-full bg-ink-800 border border-white/8 rounded-lg px-3 py-2 font-body text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold/50 transition-colors"
              />
            </div>
            <div className="space-y-1.5">
              <label className="font-body text-[10px] text-ash/60 uppercase tracking-widest">Longitude</label>
              <input
                value={lng}
                onChange={e => setLng(e.target.value)}
                placeholder="2.3522"
                className="w-full bg-ink-800 border border-white/8 rounded-lg px-3 py-2 font-body text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold/50 transition-colors"
              />
            </div>
          </div>
          {(lat.trim() || lng.trim()) && (
            <button
              onClick={() => { setLat(''); setLng(''); }}
              className="font-body text-[11px] text-ash/50 hover:text-red-300 transition-colors"
            >
              ✕ Clear coordinates
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ThemeCard({ theme, selected, onSelect }) {
  return (
    <button
      onClick={() => onSelect(theme.id)}
      className={`relative group text-left transition-all duration-300 rounded-lg overflow-hidden border ${
        selected ? 'border-gold scale-105 shadow-lg shadow-gold/20' : 'border-white/5 hover:border-white/20'
      }`}
    >
      <div className="h-16 w-full" style={{ background: THEME_SWATCHES[theme.id] || '#1a1a2a' }} />
      <div className="p-2.5 bg-ink-800">
        <p className="font-body text-xs font-medium text-white/90 leading-tight">{theme.name}</p>
        <p className="font-body text-[10px] text-ash mt-0.5 leading-snug">{theme.description}</p>
      </div>
      {selected && (
        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-gold flex items-center justify-center">
          <svg className="w-3 h-3 text-ink-950" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      )}
    </button>
  );
}

function PosterCard({ poster, onDelete }) {
  const [hover, setHover] = useState(false);
  const name = poster.filename.replace('.png', '');
  const parts = name.split('_');
  const city = parts[0];
  const theme = parts.slice(1, -2).join('_');

  return (
    <div
      className="group relative rounded-xl overflow-hidden border border-white/5 hover:border-gold/40 transition-all duration-500 bg-ink-800"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="relative overflow-hidden" style={{ paddingBottom: '133%' }}>
        <img src={poster.url} alt={poster.filename}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className={`absolute inset-0 bg-ink-950/70 flex flex-col items-center justify-center gap-3 transition-opacity duration-300 ${hover ? 'opacity-100' : 'opacity-0'}`}>
          <a href={poster.url} download={poster.filename}
            className="flex items-center gap-2 px-4 py-2 bg-gold text-ink-950 rounded-lg font-body text-sm font-medium hover:bg-gold-light transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download
          </a>
          <button onClick={() => onDelete(poster.filename)}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white/70 rounded-lg font-body text-sm hover:bg-red-900/40 hover:text-red-300 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </button>
        </div>
      </div>
      <div className="p-3">
        <p className="font-display text-sm capitalize text-white/90 truncate">{city}</p>
        <p className="font-body text-xs text-ash capitalize mt-0.5">{theme.replace(/_/g, ' ')}</p>
      </div>
    </div>
  );
}

function LiveLog({ logLines }) {
  const logEndRef = useRef(null);
  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [logLines]);
  return (
    <div className="w-full rounded-lg bg-ink-950 border border-white/8 overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/5 bg-ink-900">
        <div className="w-2.5 h-2.5 rounded-full bg-red-500/50" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-500/50" />
        <span className="ml-2 font-body text-[10px] text-ash/40 tracking-widest uppercase">output</span>
      </div>
      <div className="h-40 overflow-y-auto p-3 space-y-0.5">
        {logLines.length === 0
          ? <p className="font-body text-[11px] text-ash/30 italic">Waiting for output…</p>
          : logLines.map((line, i) => (
              <p key={i} className="font-body text-[11px] text-ash/75 leading-relaxed whitespace-pre-wrap break-all">
                <span className="text-gold/30 select-none mr-2">›</span>{line}
              </p>
            ))}
        <div ref={logEndRef} />
      </div>
    </div>
  );
}

function LoadingOrb({ logLines }) {
  return (
    <div className="flex flex-col items-center py-8 gap-5 px-4">
      <div className="relative w-14 h-14 flex-shrink-0">
        <div className="absolute inset-0 rounded-full border-2 border-gold/20 animate-ping" />
        <div className="absolute inset-1 rounded-full border-2 border-t-gold border-r-gold border-b-transparent border-l-transparent animate-spin" />
        <div className="absolute inset-3 rounded-full border border-gold/40 animate-spin-slow" style={{ animationDirection: 'reverse' }} />
        <div className="absolute inset-4 rounded-full bg-gold/10 animate-pulse-slow" />
      </div>
      <p className="font-display text-base text-gold">Rendering your city…</p>
      <LiveLog logLines={logLines} />
    </div>
  );
}

export default function App() {
  const [themes, setThemes] = useState([]);
  const [posters, setPosters] = useState([]);
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('midnight_blue');
  const [selectedFont, setSelectedFont] = useState(null);
  const [selectedSize, setSelectedSize] = useState(SIZE_PRESETS[0]);
  const [distance, setDistance] = useState(12000);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [tab, setTab] = useState('generate');
  const [jobStatus, setJobStatus] = useState(null);
  const [resultUrl, setResultUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [logLines, setLogLines] = useState([]);
  const [notification, setNotification] = useState('');
  const sseRef = useRef(null);

  useEffect(() => {
    if (!document.getElementById('gfonts-poster')) {
      const link = document.createElement('link');
      link.id = 'gfonts-poster';
      link.rel = 'stylesheet';
      link.href = GFONTS_URL;
      document.head.appendChild(link);
    }
    fetch(`${API}/themes`).then(r => r.json()).then(setThemes).catch(() => {});
    loadPosters();
  }, []);

  const loadPosters = () => {
    fetch(`${API}/posters-list`).then(r => r.json()).then(setPosters).catch(() => {});
  };

  const notify = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(''), 3000);
  };

  const startSSE = (jobId) => {
    if (sseRef.current) sseRef.current.close();
    const es = new EventSource(`${API}/job/${jobId}/stream`);
    sseRef.current = es;
    es.addEventListener('log', (e) => {
      const line = e.data.trim();
      if (line) setLogLines(prev => [...prev, line]);
    });
    es.addEventListener('done', (e) => {
      setJobStatus('done');
      setResultUrl(e.data);
      loadPosters();
      notify('Poster generated!');
      es.close();
    });
    es.addEventListener('error', (e) => {
      if (e.data) { setJobStatus('error'); setErrorMsg(e.data); }
      es.close();
    });
  };

  const handleGenerate = async () => {
    if (!city.trim() || !country.trim()) return;
    setJobStatus('pending');
    setResultUrl(null);
    setErrorMsg('');
    setLogLines([]);

    try {
      const body = {
        city, country,
        theme: selectedTheme,
        distance,
        width: selectedSize.width,
        height: selectedSize.height,
      };
      if (selectedFont) body.font_family = selectedFont;
      if (lat.trim() && lng.trim()) {
        body.latitude = parseFloat(lat);
        body.longitude = parseFloat(lng);
      }

      const res = await fetch(`${API}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setJobStatus('running');
      startSSE(data.job_id);
    } catch {
      setJobStatus('error');
      setErrorMsg('Could not connect to backend.');
    }
  };

  const handleDelete = async (filename) => {
    await fetch(`${API}/posters/${filename}`, { method: 'DELETE' });
    loadPosters();
    notify('Poster deleted');
  };

  const isGenerating = jobStatus === 'pending' || jobStatus === 'running';

  return (
    <div className="min-h-screen bg-ink-950 text-white">
      {notification && (
        <div className="fixed top-6 right-6 z-50 px-5 py-3 bg-gold text-ink-950 rounded-lg font-body text-sm font-medium shadow-xl animate-fade-up">
          {notification}
        </div>
      )}

      <header className="border-b border-white/5 sticky top-0 z-40 bg-ink-950/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-gradient-to-br from-gold/60 to-gold/20 flex items-center justify-center">
              <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <div>
              <h1 className="font-display text-lg font-semibold text-white leading-none">MapToPoster</h1>
              <p className="font-body text-[10px] text-ash tracking-widest uppercase mt-0.5">City Art Generator</p>
            </div>
          </div>
          <nav className="flex gap-1 bg-ink-800 rounded-lg p-1">
            {['generate', 'gallery'].map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`px-4 py-1.5 rounded-md font-body text-sm capitalize transition-all duration-200 ${
                  tab === t ? 'bg-gold text-ink-950 font-medium' : 'text-ash hover:text-white'
                }`}>
                {t === 'gallery' ? `Gallery (${posters.length})` : t}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {tab === 'generate' && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Left: form */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h2 className="font-display text-4xl font-semibold leading-tight">
                  Turn any city<br />
                  <em className="text-gold not-italic">into art.</em>
                </h2>
                <p className="font-body text-sm text-ash mt-3 leading-relaxed">
                  Generate a high-resolution minimalist map poster from OpenStreetMap data.
                </p>
              </div>

              {/* City / Country */}
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="font-body text-xs text-ash tracking-widest uppercase">City</label>
                  <input value={city} onChange={e => setCity(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleGenerate()}
                    placeholder="Paris"
                    className="w-full bg-ink-800 border border-white/8 rounded-lg px-4 py-3 font-body text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold/50 transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <label className="font-body text-xs text-ash tracking-widest uppercase">Country</label>
                  <input value={country} onChange={e => setCountry(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleGenerate()}
                    placeholder="France"
                    className="w-full bg-ink-800 border border-white/8 rounded-lg px-4 py-3 font-body text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold/50 transition-colors" />
                </div>
              </div>

              {/* Radius */}
              <div className="space-y-1.5">
                <label className="font-body text-xs text-ash tracking-widest uppercase flex justify-between">
                  <span>Radius</span>
                  <span className="text-gold">{(distance / 1000).toFixed(0)} km</span>
                </label>
                <input type="range" min="3000" max="25000" step="1000"
                  value={distance} onChange={e => setDistance(Number(e.target.value))}
                  className="w-full accent-gold h-1 rounded-full bg-ink-700 cursor-pointer" />
                <div className="flex justify-between font-body text-[10px] text-ash/60">
                  <span>3 km — dense</span>
                  <span>25 km — full metro</span>
                </div>
              </div>

              {/* Size presets */}
              <SizeSelector selected={selectedSize} onSelect={setSelectedSize} />

              {/* Font selector */}
              <FontSelector selected={selectedFont} onSelect={setSelectedFont} />

              {/* Advanced */}
              <AdvancedSection lat={lat} setLat={setLat} lng={lng} setLng={setLng} />

              {/* Generate button */}
              <button onClick={handleGenerate}
                disabled={isGenerating || !city || !country}
                className="w-full py-4 bg-gold text-ink-950 font-body font-semibold text-sm rounded-lg
                  hover:bg-gold-light transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed
                  flex items-center justify-center gap-2 shadow-lg shadow-gold/20 active:scale-98">
                {isGenerating ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Generating…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
                    </svg>
                    Generate Poster
                  </>
                )}
              </button>

              {jobStatus === 'error' && (
                <div className="p-4 rounded-lg bg-red-950/50 border border-red-800/50">
                  <p className="font-body text-xs text-red-300 font-medium mb-1">Generation failed</p>
                  <p className="font-body text-xs text-red-400/70 break-all">{errorMsg}</p>
                </div>
              )}
            </div>

            {/* Right: preview + themes */}
            <div className="lg:col-span-3 space-y-8">
              <div className="rounded-2xl overflow-hidden border border-white/5 bg-ink-800 min-h-64">
                {isGenerating && <LoadingOrb logLines={logLines} />}
                {!isGenerating && resultUrl && (
                  <div className="relative group">
                    <img src={resultUrl} alt="Generated poster" className="w-full" />
                    <div className="absolute inset-0 bg-ink-950/0 group-hover:bg-ink-950/60 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <a href={resultUrl} download
                        className="flex items-center gap-2 px-5 py-3 bg-gold text-ink-950 rounded-xl font-body text-sm font-semibold hover:bg-gold-light transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download Poster
                      </a>
                    </div>
                  </div>
                )}
                {!isGenerating && !resultUrl && (
                  <div className="flex flex-col items-center justify-center h-64 gap-3 text-ash">
                    <svg className="w-12 h-12 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    <p className="font-body text-sm opacity-40">Your poster will appear here</p>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-px flex-1 bg-white/5" />
                  <h3 className="font-body text-xs text-ash tracking-widest uppercase px-2">Select Theme</h3>
                  <div className="h-px flex-1 bg-white/5" />
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1">
                  {themes.map(t => (
                    <ThemeCard key={t.id} theme={t} selected={selectedTheme === t.id} onSelect={setSelectedTheme} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'gallery' && (
          <div>
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="font-display text-3xl font-semibold">Gallery</h2>
                <p className="font-body text-sm text-ash mt-1">{posters.length} poster{posters.length !== 1 ? 's' : ''} generated</p>
              </div>
              <button onClick={loadPosters} className="font-body text-xs text-ash hover:text-gold transition-colors flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
            {posters.length === 0 ? (
              <div className="text-center py-20 text-ash">
                <svg className="w-16 h-16 mx-auto opacity-20 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="font-body text-sm">No posters generated yet.<br />Head to the Generate tab to create your first.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
                {posters.map(p => (
                  <PosterCard key={p.filename} poster={p} onDelete={handleDelete} />
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-white/5 mt-20 py-6 text-center font-body text-xs text-ash/40">
        Powered by <span className="text-ash/70">OpenStreetMap</span> & <span className="text-ash/70">maptoposter</span> — MIT License
      </footer>
    </div>
  );
}
